import {
  Box,
  Typography,
  ThemeProvider,
  SelectChangeEvent,
  Stack,
  Chip,
} from "@mui/material";
import "../style.css";
import { useEffect, useRef, useState } from "react";
import MUIDataTable from "mui-datatables";
import ROUTE_NAMES from "routes/routeNames";
import { getTableTheme } from "../tableTheme";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency, isStoredMediaUrl } from "utils/commonFunctions";
import { commonTableStyles as styles, TABLE_TOKENS } from "../styles";
import { CouponListItem, ReducersModal } from "utils/modals";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteCoupon, dispatchCouponValue } from "modules/coupon/action";
import ContainedButton from "components/button/contained-button";
import DeleteEditModal from "components/customModal/DeleteEditModal";
import { CATEGORY_DATA, formateDate } from "utils/constants";
import {
  getDiscountStatusColor,
  getDiscountStatusLabel,
} from "utils/userType";
import TableFilterSelect from "../TableFilterSelect";

type CouponTableProps = {
  onAddPress?: () => void;
  onEditPress?: (row: CouponListItem) => void;
};

const SEARCH_DEBOUNCE_MS = 500;

const CouponTable = ({ onAddPress, onEditPress }: CouponTableProps) => {
  const { limit, page, result, search, totalRecords, category } = useSelector(
    (state: ReducersModal) => state.CouponReducer
  );

  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [deleteRow, setDeleteRow] = useState<CouponListItem | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef(search || "");

  const clearPendingSearch = () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  };

  useEffect(() => clearPendingSearch, []);

  const dispatchSearchValue = (text: string) => {
    lastSearchRef.current = text;
    dispatch(dispatchCouponValue({ search: text || "", page: 1 }));
  };

  const handleSearchChange = (text: string | null) => {
    const nextText = `${text || ""}`.trim();
    clearPendingSearch();
    if (nextText === lastSearchRef.current) return;
    searchTimerRef.current = setTimeout(() => {
      dispatchSearchValue(nextText);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleChangePage = (newPage: number) => {
    dispatch(dispatchCouponValue({ page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (numberOfRows: number) => {
    dispatch(dispatchCouponValue({ limit: numberOfRows, page: 1 }));
  };

  const categoryHandler = (event: SelectChangeEvent) => {
    dispatch(dispatchCouponValue({ category: event.target.value, page: 1 }));
  };

  const resetFilters = () => {
    dispatch(
      dispatchCouponValue({
        category: "-1",
        page: 1,
      })
    );
  };

  const goToDetails = (row: CouponListItem) => {
    navigate(`${ROUTE_NAMES.MAIN_ROUTES.COUPON_DETAIL}?couponId=${row._id}`);
  };

  const confirmDelete = () => {
    if (!deleteRow?._id) return;
    const s3Urls = [deleteRow.image].filter((url) =>
      isStoredMediaUrl(url)
    ) as string[];
    dispatch(deleteCoupon(deleteRow._id, () => {}, s3Urls));
    setDeleteRow(null);
  };

  const columns: any = [
    {
      name: "couponCode",
      label: "Coupon Code",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any, tableMeta: any) => {
          const row: CouponListItem = tableMeta.tableData[tableMeta.rowIndex];
          if (value)
            return (
              <div className="name-field" onClick={() => goToDetails(row)}>
                {value}
                {row?.isDefault ? <Chip
                  size="small"
                  variant="filled"
                  color="success"
                  label="Default"
                  sx={{ ...styles.tableChip, ml: 1 }}
                />
                  : <></>}
              </div>
            );
          return <>N/A</>;
        },
      },
    },
    {
      name: "title",
      label: "Title",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => <div>{value || "N/A"}</div>,
      },
    },
    {
      name: "couponValue",
      label: "Coupon Value",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any, tableMeta: any) => {
          const row: CouponListItem = tableMeta.tableData[tableMeta.rowIndex];
          if (value !== 0 && !value) return <div>N/A</div>;
          return (
            <div>
              {row.isPercentage ? `${value}%` : formatCurrency(Number(value))}
            </div>
          );
        },
      },
    },
    {
      name: "startTime",
      label: "Start Time",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => (
          <div>{formateDate(value)}</div>
        ),
      },
    },
    {
      name: "endTime",
      label: "End Time",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => (
          <div>{formateDate(value)}</div>
        ),
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (value: any) => (
          <Chip
            size="small"
            variant="outlined"
            color={getDiscountStatusColor(value)}
            label={getDiscountStatusLabel(value)}
            sx={styles.tableChip}
          />
        ),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row: CouponListItem = tableMeta.tableData[tableMeta.rowIndex];
          return (
            <div className="icon-container">
              <BorderColorIcon
                style={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                onClick={() => {
                  if (onEditPress) {
                    onEditPress(row);
                    return;
                  }
                  navigate(
                    `${ROUTE_NAMES.MAIN_ROUTES.EDIT_COUPON}?couponId=${row._id}`,
                    {
                      state: {
                        couponDetails: row,
                      },
                    }
                  );
                }}
              />
              <VisibilityIcon
                style={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                onClick={() => goToDetails(row)}
              />
              <DeleteOutlineIcon
                style={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                onClick={() => setDeleteRow(row)}
              />
            </div>
          );
        },
      },
    },
    {
      name: "Category",
      options: {
        filter: true,
        display: "excluded",
        filterType: "custom",
        filterList: [category],
        filterOptions: {
          fullWidth: false,
          logic: () => false,
          display: () => (
            <TableFilterSelect
              label="Category"
              value={category}
              options={CATEGORY_DATA}
              onChange={categoryHandler}
            />
          ),
        },
      },
    },
  ];

  const options: any = {
    print: false,
    filter: true,
    download: false,
    serverSide: true,
    viewColumns: false,
    selectableRows: "none",
    filterType: "dropdown",
    count: totalRecords,
    page: page - 1,
    rowsPerPage: limit,
    onChangePage: (currentPage: number) => handleChangePage(currentPage),
    rowsPerPageOptions: [10, 25, 50, 100],
    onChangeRowsPerPage: (numberOfRows: number) =>
      handleChangeRowsPerPage(numberOfRows),
    search: true,
    searchPlaceholder: "Search by Coupon Code...",
    onSearchChange: (text: string | null) => handleSearchChange(text),
    onSearchClose: () => {
      clearPendingSearch();
      if (lastSearchRef.current) dispatchSearchValue("");
    },
    onFilterChange: (_: string, _filterList: any[], type: any) => {
      if (type === "reset") {
        resetFilters();
      }
    },
    customToolbar: () =>
      onAddPress ? (
        <ContainedButton
          externalStyles={styles.toolbarAddBtn}
          onClick={onAddPress}
          title="Create coupon"
        />
      ) : null,
  };

  const TableComponent: any = MUIDataTable;
  return (
    <Box sx={styles.paper}>
      <ThemeProvider theme={getTableTheme()}>
        <TableComponent
          title={
            <Stack direction={"row"} spacing={2}>
              <Typography variant="h5" sx={styles.tableHeadingTxt}>
                {`Coupon List`}
              </Typography>
            </Stack>
          }
          data={result}
          columns={columns}
          options={options}
        />
      </ThemeProvider>
      <DeleteEditModal
        onClose={() => setDeleteRow(null)}
        title="Delete Confirmation"
        children={"Are you sure you want to delete this coupon?"}
        visible={Boolean(deleteRow)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default CouponTable;
